import { Menu, Transition } from '@headlessui/react';
import Down from 'assets/icons/Down';
import Up from 'assets/icons/Up';
import Chip from 'common/components/Chip';
import InlineLoader from 'common/components/InlineLoader';
import useTranslation from 'common/hooks/useTranslation';
import { cn } from 'common/utils';
import React, { Fragment, useState } from 'react';
import { useEffect } from 'react';

const useChipDropdown = (contextTranslationKey, options) => {
  const [showDropDown, setShowDropDown] = useState(false);
  const { t } = useTranslation(contextTranslationKey);

  useEffect(() => {
    if (options && options.length > 0) {
      setShowDropDown(true);
    }
  }, [options]);

  return { t, showDropDown };
};

const ChipDropdown = ({
  placeholder,
  isLoading = false,
  currentOption,
  options,
  onChange,
  chipClassName,
  chipVariant,
  contextTranslationKey,
  dropDownPosition = 'right',
  hasError = false,
}) => {
  const { t, showDropDown } = useChipDropdown(contextTranslationKey, options);

  return (
    <>
      {showDropDown && (
        <Menu as='div' className='relative inline-block max-w-fit'>
          {({ open }) => (
            <>
              <Menu.Button
                aria-disabled={isLoading}
                disabled={isLoading}
                className={cn(
                  'h-full max-w-full',
                  isLoading && 'cursor-not-allowed opacity-50',
                )}
              >
                <Chip
                  variant={chipVariant}
                  className={cn(
                    chipClassName,
                    'max-w-full transition-color duration-200',
                    open && 'border-dark-blue text-dark-blue',
                    !currentOption && 'text-gray-400',
                    hasError &&
                      'border-bittersweet-shimmer text-bittersweet-shimmer',
                  )}
                >
                  <div className='flex items-center gap-1 max-w-full'>
                    <div className='max-w-full truncate'>
                      {currentOption ? t(currentOption.label) : t(placeholder)}
                    </div>
                    {isLoading ? (
                      <InlineLoader size={16} />
                    ) : open ? (
                      <Up size={16} />
                    ) : (
                      <Down size={16} />
                    )}
                  </div>
                </Chip>
              </Menu.Button>

              <Transition
                as={Fragment}
                enter='transition ease-out duration-100'
                enterFrom='transform opacity-0 scale-95'
                enterTo='transform opacity-100 scale-100'
                leave='transition ease-in duration-75'
                leaveFrom='transform opacity-100 scale-100'
                leaveTo='transform opacity-0 scale-95'
              >
                <Menu.Items
                  className={`absolute z-50 ${dropDownPosition}-0 mt-2 min-w-full truncate origin-top-right divide-y divide-gray-200 bg-white rounded-md shadow-lg ring-1 ring-dark-charcoal ring-opacity-5 focus:outline-none`}
                >
                  {options.map((option, index) => (
                    <Menu.Item key={index}>
                      {({ active }) => (
                        <button
                          onClick={() => onChange(option.key)}
                          className={cn(
                            'flex w-full items-center px-4 py-4 text-sm gap-2.5 text-dark-charcoal',
                            option.key === currentOption?.key && 'bg-gray-200',
                            active && 'bg-vibrant-orange bg-opacity-10',
                          )}
                        >
                          {t(option.label)}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </>
          )}
        </Menu>
      )}
    </>
  );
};

export default ChipDropdown;
